import type { CallbackTicket } from '@/services/callbackTicketService';
import type { ConversationListItem } from '@/services/conversationService';
import type { LiveCallSummary, LiveOverview, WaitingContact } from '@/services/liveOverviewService';
import { ticketSelection } from './callbackTicket';
import { dayKey, phoneDigits } from './conversationFormat';
import { isRecentlyActive } from './liveConversation';
import type { Selection } from './conversationUrl';

/**
 * The sections of the Verläufe list: what needs the salon now pinned on top
 * (from the live overview), then the contacts/calls (`group=contact`) grouped
 * by day. One filter chip at a time.
 *
 * „Wartet auf Rückmeldung“ holds both callback kinds: WhatsApp contacts
 * (`waiting`, state callback) and the phone agent's Rückruf-Tickets
 * (`callback_tickets`, shown under Alle / Wartet / Telefon).
 */

export type ListChip = 'all' | 'live' | 'waiting' | 'taken' | 'phone' | 'whatsapp';

export const LIST_CHIPS: { id: ListChip; label: string }[] = [
  { id: 'all', label: 'Alle' },
  { id: 'live', label: 'Live' },
  { id: 'waiting', label: 'Wartet' },
  { id: 'taken', label: 'Übernommen' },
  { id: 'phone', label: 'Telefon' },
  { id: 'whatsapp', label: 'WhatsApp' },
];

/** Channel the server filters by for a chip (`type=`); the other chips filter on the client. */
export function chipChannel(chip: ListChip): 'phone' | 'whatsapp' | undefined {
  return chip === 'phone' || chip === 'whatsapp' ? chip : undefined;
}

export type ListRow =
  | { kind: 'waiting'; key: string; waiting: WaitingContact }
  | { kind: 'ticket'; key: string; ticket: CallbackTicket }
  | { kind: 'call'; key: string; call: LiveCallSummary }
  | { kind: 'item'; key: string; item: ConversationListItem };

export type ListSectionId = 'requested' | 'callback' | 'calls' | 'today' | 'yesterday' | 'earlier';

export interface ListSection {
  id: ListSectionId;
  title: string;
  /** Pinned sections stand for "needs the salon now". */
  pinned: boolean;
  rows: ListRow[];
}

export interface ListInput {
  items: ConversationListItem[];
  overview: LiveOverview;
  chip: ListChip;
  /** Search text; the server already applied it to `items`. */
  query: string;
  /** Agent number filter; the server already applied it to `items`. */
  agentNumber: string;
  now: number;
}

function matchesQuery(name: string | null, phone: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if ((name ?? '').toLowerCase().includes(q)) return true;
  const digits = phoneDigits(q);
  return digits.length > 0 && phoneDigits(phone).includes(digits);
}

function matchesAgent(agentNumber: string | null, filter: string): boolean {
  if (!filter) return true;
  return phoneDigits(agentNumber) === phoneDigits(filter);
}

function byTime(a: string | null | undefined, b: string | null | undefined): number {
  return (Date.parse(a ?? '') || 0) - (Date.parse(b ?? '') || 0);
}

export function itemRowKey(item: ConversationListItem): string {
  return item.type === 'whatsapp' && item.contact_key ? `contact:${item.contact_key}` : `conversation:${item.id}`;
}

/** A waiting customer in a taken chat counts as waiting for a person too. */
export function isWaitingForStaff(waiting: WaitingContact): boolean {
  return waiting.state === 'requested' || waiting.state === 'staff';
}

/**
 * Badge count of the "Wartet" chip (after the agent filter): waiting WhatsApp
 * contacts plus open Rückruf-Tickets. The overview lists `staff` contacts only
 * while their customer waits (livecenter §5).
 */
export function waitingCount(overview: LiveOverview, agentNumber: string): number {
  return (
    overview.waiting.filter((w) => matchesAgent(w.agent_number, agentNumber)).length +
    overview.callback_tickets.filter((t) => matchesAgent(t.agent_number, agentNumber)).length
  );
}

/** The time a callback row waits since, for ordering both kinds in one section. */
function callbackSince(row: ListRow): string | null {
  if (row.kind === 'waiting') return row.waiting.waiting_since;
  if (row.kind === 'ticket') return row.ticket.created_at;
  return null;
}

function daySections(items: ConversationListItem[], now: number): ListSection[] {
  const today = dayKey(new Date(now).toISOString());
  const yesterday = dayKey(new Date(now - 24 * 3_600_000).toISOString());
  const groups: Record<'today' | 'yesterday' | 'earlier', ListRow[]> = { today: [], yesterday: [], earlier: [] };
  items.forEach((item) => {
    const day = dayKey(item.last_message_at);
    const target = day === today ? 'today' : day === yesterday ? 'yesterday' : 'earlier';
    groups[target].push({ kind: 'item', key: itemRowKey(item), item });
  });
  const titles = { today: 'Heute', yesterday: 'Gestern', earlier: 'Früher' } as const;
  return (['today', 'yesterday', 'earlier'] as const)
    .filter((id) => groups[id].length > 0)
    .map((id) => ({ id, title: titles[id], pinned: false, rows: groups[id] }));
}

export function buildListSections({ items, overview, chip, query, agentNumber, now }: ListInput): ListSection[] {
  const waiting = overview.waiting.filter(
    (w) => matchesQuery(w.customer_name, w.customer_phone, query) && matchesAgent(w.agent_number, agentNumber),
  );
  const requested = waiting
    .filter(isWaitingForStaff)
    .sort((a, b) => byTime(a.waiting_since, b.waiting_since))
    .map<ListRow>((w) => ({ kind: 'waiting', key: `waiting:${w.contact_key}`, waiting: w }));
  const callbacks = waiting
    .filter((w) => w.state === 'callback')
    .map<ListRow>((w) => ({ kind: 'waiting', key: `waiting:${w.contact_key}`, waiting: w }));
  const tickets = overview.callback_tickets
    .filter((t) => matchesQuery(t.customer_name, t.customer_phone, query) && matchesAgent(t.agent_number, agentNumber))
    .map<ListRow>((t) => ({ kind: 'ticket', key: `ticket:${t.id}`, ticket: t }));
  const calls = overview.live_calls
    .filter((c) => matchesQuery(c.customer_name, c.customer_phone, query) && matchesAgent(c.agent_number, agentNumber))
    .sort((a, b) => byTime(a.started_at, b.started_at))
    .map<ListRow>((c) => ({ kind: 'call', key: `call:${c.call_sid}`, call: c }));

  const showRequested = chip === 'all' || chip === 'live' || chip === 'waiting' || chip === 'whatsapp';
  const showCallbacks = chip === 'all' || chip === 'waiting' || chip === 'whatsapp';
  const showTickets = chip === 'all' || chip === 'waiting' || chip === 'phone';
  const showCalls = chip === 'all' || chip === 'live' || chip === 'phone';
  const pinnedRequested =
    chip === 'taken'
      ? requested.filter((r) => r.kind === 'waiting' && r.waiting.state === 'staff')
      : showRequested
        ? requested
        : [];
  const pinnedCallbacks = [...(showCallbacks ? callbacks : []), ...(showTickets ? tickets : [])].sort((a, b) =>
    byTime(callbackSince(a), callbackSince(b)),
  );

  // Only contacts pinned under THIS chip leave the list below; the others
  // (e.g. a callback contact under "Live") stay findable there. The same for
  // the stored call of a pinned Rückruf-Ticket.
  const pinnedKeys = new Set(
    [...pinnedRequested, ...pinnedCallbacks].flatMap((r) => (r.kind === 'waiting' ? [r.waiting.contact_key] : [])),
  );
  const pinnedCalls = new Set(
    pinnedCallbacks.flatMap((r) => (r.kind === 'ticket' && r.ticket.conversation_id != null ? [r.ticket.conversation_id] : [])),
  );
  const rest = items.filter(
    (item) =>
      !(item.type === 'whatsapp' && item.contact_key && pinnedKeys.has(item.contact_key)) &&
      !(item.type === 'phone' && pinnedCalls.has(item.id)),
  );

  const listed = (() => {
    switch (chip) {
      case 'waiting':
        return [];
      case 'live':
        return rest.filter((i) => i.type === 'whatsapp' && isRecentlyActive(i.last_message_at, now));
      case 'taken':
        return rest.filter((i) => i.type === 'whatsapp' && (i.takeover_state === 'staff' || i.takeover_active));
      default:
        return rest;
    }
  })();

  const sections: ListSection[] = [];
  if (pinnedRequested.length) {
    sections.push({ id: 'requested', title: 'Wartet auf Mitarbeiter', pinned: true, rows: pinnedRequested });
  }
  if (pinnedCallbacks.length) {
    sections.push({ id: 'callback', title: 'Wartet auf Rückmeldung', pinned: true, rows: pinnedCallbacks });
  }
  if (showCalls && calls.length) {
    sections.push({ id: 'calls', title: 'Anruf läuft', pinned: true, rows: calls });
  }
  return [...sections, ...daySections(listed, now)];
}

/** Row order as rendered, for keyboard navigation. */
export function flattenRows(sections: ListSection[]): ListRow[] {
  return sections.flatMap((s) => s.rows);
}

/** What opens for a list entry: the contact chat for WhatsApp, the stored call otherwise. */
export function selectionForItem(item: ConversationListItem): Selection {
  if (item.type === 'whatsapp' && item.contact_key) return { kind: 'contact', key: item.contact_key };
  return { kind: 'conversation', id: item.id };
}

export function selectionForRow(row: ListRow): Selection {
  if (row.kind === 'waiting') return { kind: 'contact', key: row.waiting.contact_key };
  if (row.kind === 'ticket') return ticketSelection(row.ticket);
  if (row.kind === 'call') return { kind: 'call', sid: row.call.call_sid };
  return selectionForItem(row.item);
}

export function isRowSelected(row: ListRow, selection: Selection | null): boolean {
  if (!selection) return false;
  if (row.kind === 'ticket') {
    // The ticket's call may be open by its stored transcript or by its sid (bell link, live call).
    if (selection.kind === 'conversation') return row.ticket.conversation_id === selection.id;
    return selection.kind === 'call' && row.ticket.call_sid === selection.sid;
  }
  if (selection.kind === 'contact') {
    return (
      (row.kind === 'waiting' && row.waiting.contact_key === selection.key) ||
      (row.kind === 'item' && row.item.type === 'whatsapp' && row.item.contact_key === selection.key)
    );
  }
  if (selection.kind === 'call') return row.kind === 'call' && row.call.call_sid === selection.sid;
  return row.kind === 'item' && row.item.id === selection.id && !(row.item.type === 'whatsapp' && row.item.contact_key);
}

/**
 * The ONE row marked as open: a ticket points at a call that may also have its
 * own row (running call), so the call or conversation itself wins over a
 * ticket; otherwise the first match in render order.
 */
export function selectedRowKey(sections: ListSection[], selection: Selection | null): string | null {
  if (!selection) return null;
  const matches = flattenRows(sections).filter((row) => isRowSelected(row, selection));
  return (matches.find((row) => row.kind !== 'ticket') ?? matches[0])?.key ?? null;
}

/**
 * The entry that best matches a jump from the dashboard / transfer banner:
 * same number, and — when several share it — the one closest in time to `at`.
 */
export function pickBestMatch(
  items: ConversationListItem[],
  phone: string,
  at: string | null,
): ConversationListItem | null {
  const digits = phoneDigits(phone);
  const matches = digits
    ? items.filter((i) => {
        const own = phoneDigits(i.customer_phone);
        return own.includes(digits) || digits.includes(own);
      })
    : items;
  if (matches.length === 0) return null;
  const target = at ? Date.parse(at) : NaN;
  if (Number.isNaN(target)) return matches[0]; // list is newest-first
  return matches.reduce((best, cur) => {
    const tCur = Date.parse(cur.last_message_at ?? '') || 0;
    const tBest = Date.parse(best.last_message_at ?? '') || 0;
    return Math.abs(tCur - target) < Math.abs(tBest - target) ? cur : best;
  });
}

/** A running call of this number, if any (the transfer banner jumps to live calls). */
export function findLiveCall(overview: LiveOverview, phone: string): LiveCallSummary | null {
  const digits = phoneDigits(phone);
  if (!digits) return null;
  return overview.live_calls.find((c) => phoneDigits(c.customer_phone) === digits) ?? null;
}
